import type { Page } from '@namu/protocol'
import { ApiError, path } from './client'
import type { ApiClient, Query } from './client'
import type {
  ConversationDetail,
  ConversationSummary,
  ConversationTurn,
  Instant,
  KnowledgeCitation,
  Locale,
  TranscriptRefusal,
} from './types'

/**
 * What the community has been talking to Kura about.
 *
 * This is the module where it would be easiest to do harm, so the split runs
 * through the API rather than being a rule somebody remembers.
 *
 * `list` carries no words. It returns when a conversation happened, how long it
 * lasted, in what language, and whether a rule was raised for a human. It is
 * still a read of individual records, so the server requires
 * `member:read_identified` and answers a `viewer` with 403; `stats` is the same
 * question asked in a form that is safe for every role, and it is what a screen
 * should reach for when it needs figures rather than rows.
 *
 * `get` returns words, and it is a different kind of act entirely. It is
 * refused unless that member granted both `store_transcripts` and
 * `share_with_org` to this organization, and every single call writes an audit
 * event: a read, not a write, because "who opened this person's transcript" is
 * a protection question and a log that only records writes cannot answer it.
 *
 * The console's own duty is smaller and comes first: `ConversationSummary`
 * carries `sharesTranscripts`, so a row without the grant does not render a
 * link that can only ever fail. Offering a door that is locked teaches people
 * to try it.
 *
 * ## Why every response here goes through a reader
 *
 * The console and the server were written in parallel against one document and
 * disagreed about roughly half of it, and the way that surfaced was somebody
 * opening a screen and being shown "This screen stopped working". The shape of
 * that failure is worth naming, because the interesting case is not a 404: a
 * 200 whose body is missing one field breaks a screen exactly as hard, and it
 * does it from inside a render, where there is no `catch` to put a message in.
 * `stats.byDay.length` against a body with no `byDay` is a blank console.
 *
 * So nothing below hands a parsed body to a screen. Each reader takes
 * `unknown`, keeps what it recognises and substitutes an honest empty value for
 * the rest, so the four screens that read these figures (the dashboard, the
 * project overview, the language-coverage banner and Conversations itself)
 * degrade to "no figures" instead of to a white page. It costs a hundred lines
 * once, in the one module that already has to know these shapes.
 */

export interface ConversationListQuery extends Query {
  projectId?: string
  /** ISO-8601 instants. Inclusive of `from`, exclusive of `to`. */
  from?: Instant
  to?: Instant
  locale?: Locale
  escalated?: boolean
  limit?: number
  cursor?: string
}

/** Counts over the same filter as the list, for the dashboard bands. */
export interface ConversationStats {
  conversations: number
  members: number
  medianDurationSeconds: number
  escalations: number
  byLocale: { locale: Locale; count: number }[]
  byDay: { day: string; count: number }[]
  /**
   * What the language breakdown would not show.
   *
   * The server withholds a locale bucket too few people are behind, and then
   * withholds enough more that the residual cannot be pinned on one language.
   * Reported as a count rather than left as a silent gap, because a breakdown
   * read as complete is a breakdown somebody draws a conclusion from.
   */
  withheldLocales: { locales: number; conversations: number }
}

/**
 * The most rows the server will return in one page.
 *
 * `MAX_PAGE_LIMIT` from `@namu/protocol`, applied here as a clamp rather than
 * left to the caller, because the caller that exceeded it was asking for a
 * five-hundred-row activity sample and got a 400 that reached the screen as
 * "the roster did not load". Silently capping a filter would be wrong; capping
 * a sample size is not, because that caller already reports whether it walked
 * the whole list.
 */
const MAX_LIMIT = 200

/**
 * The console's filter names, in the server's.
 *
 * `from` and `to` on this side, `since` and `until` on the wire. The console
 * keeps its own names because every date filter in it is labelled From and To,
 * and no screen should have to know that one endpoint spells them differently.
 * One translation beats a rename that would touch every caller.
 */
function windowQuery(input: ConversationListQuery): Query {
  return {
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    ...(input.locale === undefined ? {} : { locale: input.locale }),
    ...(input.from === undefined ? {} : { since: input.from }),
    ...(input.to === undefined ? {} : { until: input.to }),
  }
}

export function conversationsApi(http: ApiClient) {
  return {
    async list(query: ConversationListQuery = {}): Promise<Page<ConversationSummary>> {
      /*
       * `view=individual` is not optional and is not a caller's choice. The
       * same path answers with an aggregate object by default, and an aggregate
       * object has no `items`, so a list screen reading it renders an empty
       * table over a database full of conversations and reports that nobody has
       * ever spoken. That is precisely the divergence this module was rewritten
       * to close, so it is stated here rather than left as a parameter that
       * looks tidy to remove.
       */
      const body = await http.request<unknown>('/v1/org/conversations', {
        query: {
          ...windowQuery(query),
          view: 'individual',
          ...(query.escalated === undefined ? {} : { escalated: query.escalated }),
          ...(query.limit === undefined ? {} : { limit: Math.min(query.limit, MAX_LIMIT) }),
          ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
        },
      })
      return readPage(body)
    },

    async stats(query: ConversationListQuery = {}): Promise<ConversationStats> {
      const body = await http.request<unknown>('/v1/org/conversations/stats', {
        query: windowQuery(query),
      })
      return readStats(body)
    },

    /**
     * One conversation, words included when they may be released.
     *
     * `reason` is sent with the request and is not optional. It is written into
     * the audit event, and requiring it at the moment of opening is the
     * difference between a log that says a transcript was read and a log that
     * says why. It also makes idle browsing feel like what it is.
     *
     * It travels as a header rather than a query parameter deliberately. A
     * query string is written into every access log between this browser and
     * the database, which would put a caseworker's stated reason next to the
     * conversation id in half a dozen places that have no business holding the
     * pair. The audit event is where that belongs.
     *
     * A refusal arrives as a 200 carrying `withheld`, not as an error, and that
     * is the server being careful rather than lax. An empty transcript is
     * indistinguishable from a fault, and the reliable consequence of shipping
     * something that looks like a fault is that somebody eventually "fixes" it.
     */
    async get(id: string, reason: string): Promise<ConversationDetail> {
      const body = await http.request<unknown>(path`/v1/org/conversations/${id}`, {
        headers: { 'x-namu-reason': reason },
      })
      return readDetail(body)
    },
  }
}

export type ConversationsApi = ReturnType<typeof conversationsApi>

/* ===================== Readers ===================== */

/*
 * Deliberately not a schema library and deliberately not throwing. Each of
 * these answers one question, which is whether a field is the type the screen
 * is about to treat it as, and a validator that refused a bad body would put it
 * back where it started, because an exception raised while rendering is the
 * blank page these exist to prevent. What a screen needs is a value, always,
 * and the honest empty one when the server sent something else.
 */

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

/** Non-finite excluded too: a `NaN` reaching a duration renders as "NaN:NaN". */
function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function flag(value: unknown): boolean {
  return value === true
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/**
 * The `items` of a list route, where an empty array is a claim rather than a
 * gap in a row.
 *
 * `list` above is right for `byDay`, `byLocale` and the citations on a
 * conversation: a missing one of those costs a chart or a column, and throwing
 * over it would take the screen down for a detail nobody came for. `items` is
 * different in kind. Coalescing it leaves the conversations screen saying
 * nobody has spoken to Kura this month, in a product whose entire claim is that
 * it is listening, and a programme officer reads that as the finding rather
 * than as a response the console could not parse.
 *
 * `api/cases.ts` argues this at length for its own list and is worth reading;
 * this is the same rule applied here.
 */
function pageItems(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw unreadable()
  return value
}

/** A 200 this console cannot read. `retry`, because a proxy or a half-finished
 * deploy is the realistic cause rather than a request that can never work. */
function unreadable(): ApiError {
  return new ApiError({
    status: 200,
    code: 'internal',
    message: 'Namu sent an answer this console could not read.',
    recovery: 'retry',
  })
}

function readSummary(value: unknown): ConversationSummary {
  const row = record(value)
  return {
    id: text(row.id),
    projectId: text(row.projectId),
    projectName: text(row.projectName),
    // Null rather than empty, because the roster column that reads this has to
    // tell "this caller is not told who spoke" from "an account with a blank
    // id", and it says something different for each.
    accountId: typeof row.accountId === 'string' ? row.accountId : null,
    startedAt: text(row.startedAt),
    durationSeconds: count(row.durationSeconds),
    locale: text(row.locale),
    turnCount: count(row.turnCount),
    escalated: flag(row.escalated),
    /*
     * Absent means not shared, and the direction of that default is the point.
     * This field decides whether a control that opens somebody's words is drawn
     * at all, so a missing field that defaulted to true would offer a way into
     * a conversation nobody agreed to release and deliver the refusal after the
     * click, which teaches people the refusals are noise.
     */
    sharesTranscripts: flag(row.sharesTranscripts),
  }
}

function readPage(value: unknown): Page<ConversationSummary> {
  const body = record(value)
  const nextCursor = body.nextCursor
  return {
    items: pageItems(body.items).map(readSummary),
    ...(typeof nextCursor === 'string' ? { nextCursor } : {}),
  }
}

function readStats(value: unknown): ConversationStats {
  const body = record(value)
  const withheld = record(body.withheldLocales)
  return {
    conversations: count(body.conversations),
    members: count(body.members),
    medianDurationSeconds: count(body.medianDurationSeconds),
    escalations: count(body.escalations),
    byLocale: list(body.byLocale)
      .map((entry) => record(entry))
      .map((entry) => ({ locale: text(entry.locale), count: count(entry.count) }))
      // A bucket with no language on it cannot be labelled and cannot be a
      // coverage gap, so it is dropped rather than drawn as a nameless bar.
      .filter((entry) => entry.locale !== ''),
    byDay: list(body.byDay)
      .map((entry) => record(entry))
      .map((entry) => ({ day: text(entry.day), count: count(entry.count) }))
      .filter((entry) => entry.day !== ''),
    withheldLocales: {
      locales: count(withheld.locales),
      conversations: count(withheld.conversations),
    },
  }
}

const REFUSALS: readonly TranscriptRefusal[] = [
  'store_transcripts_not_granted',
  'share_with_org_not_granted',
  'nothing_was_recorded',
]

function readTurn(value: unknown, index: number): ConversationTurn {
  const turn = record(value)
  return {
    /*
     * Position, because a turn has no id of its own on the wire and React needs
     * a key. Stable is the requirement rather than globally unique: the drawer
     * discards its state when it is pointed at a different conversation, so an
     * index never outlives the list it indexes.
     */
    key: `${count(turn.ordinal)}-${index}`,
    ordinal: count(turn.ordinal),
    speaker: turn.role === 'assistant' ? 'kura' : 'member',
    text: typeof turn.text === 'string' ? turn.text : null,
    startedAt: text(turn.startedAt),
    interrupted: flag(turn.interrupted),
  }
}

function readCitation(value: unknown): KnowledgeCitation {
  const citation = record(value)
  return {
    versionId: text(citation.versionId),
    documentId: text(citation.documentId),
    title: text(citation.title, 'Untitled'),
    version: count(citation.version),
  }
}

function readDetail(value: unknown): ConversationDetail {
  const body = record(value)
  const withheld = record(body.withheld)
  const reason = REFUSALS.find((known) => known === withheld.reason)

  return {
    conversation: readSummary(body.conversation),
    /*
     * Null and empty are different answers and are kept apart. Null is "no
     * transcript was released at all"; empty is "it was released and there is
     * nothing in it", which is what a session that ran without permission to
     * keep words leaves behind. The panel says something different for each,
     * and collapsing them here would leave it nothing to say.
     */
    transcript: Array.isArray(body.transcript) ? body.transcript.map(readTurn) : null,
    knowledge: list(body.knowledge).map(readCitation),
    withheld:
      reason === undefined
        ? null
        : {
            reason,
            /*
             * The server's own sentence when it sent one, because it knows
             * which of the two grants is missing and this console does not.
             * A generic message here would flatten "nothing was ever written
             * down", which nobody can undo, into "go and ask them", which
             * somebody can act on.
             */
            message: text(
              withheld.message,
              'This conversation cannot be opened without permission from the member.',
            ),
          },
  }
}
